import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Play, Check, ArrowRight } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

export default function HowQorvixWorks() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [modalButtonType, setModalButtonType] = useState('demo');
  const [isVideo1Playing, setIsVideo1Playing] = useState(false);
  const [isVideo2Playing, setIsVideo2Playing] = useState(false);
  const [isVideo3Playing, setIsVideo3Playing] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const systemFeatures = [
    "Unified Inbox Engine",
    "AI Decision Core",
    "Behavioral Memory Layer",
    "Booking & Payment Automation",
    "Live CRM & Revenue Intelligence"
  ];

  const communicationFeatures = [
    "WhatsApp, website, ads & email fully synchronized",
    "One shared live inbox for all teams",
    "Automatic identity creation for every lead"
  ];

  const aiFeatures = [
    "Natural language buyer intent detection",
    "Automatic buyer classification & deal scoring",
    "Live CRM intelligence update"
  ];

  const behavioralFeatures = [
    "Non-scripted follow-up automation",
    "Lead resurrection & deal recovery",
    "Reduced ghosting and abandoned pipelines"
  ];

  const executionFeatures = [
    "Live calendar synchronization",
    "Automated confirmations and reminders",
    "Integrated transaction verification"
  ];

  const intelligenceFeatures = [
    "Live pipeline tracking",
    "Conversion analytics & campaign ROI",
    "Predictive revenue forecasting"
  ];

  return (
    <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dashboard-image {
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo-qorvix.png"
                alt="Qorvix Logo"
                className="h-10 w-auto"
              />
            </Link>
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
              <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
              <Link to="/how-it-works" className="text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
              <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
              <div className="relative group">
                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                  Resources
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {/* Dropdown Menu */}
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <a href="#documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                    <a href="#integration" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</a>
                    <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                    <a href="#help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                    <a href="#roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</a>
                  </div>
                </div>
              </div>
              <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
              <button onClick={() => {
                setModalButtonType('demo');
                setIsSignupModalOpen(true);
              }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                Start free demo
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-900"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">How Qorvix Works</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Everything you need to set up, manage, and scale with Qorvix. From quick start guides to advanced troubleshooting.
          </p>
        </div>
      </section>

      {/* Section 1: Unified Communication Infrastructure */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Unified Communication Infrastructure
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              Qorvix connects directly to WhatsApp Business API, website forms, landing pages, ad platforms, and email inboxes. Every incoming message is routed into the same centralized communication engine. Instead of splitting conversations across personal phones, laptops, staff inboxes, and dashboards, Qorvix creates one synchronized environment where every interaction is captured, stored, and processed in real time.
            </p>
            <div className="space-y-3 max-w-2xl mx-auto mb-12">
              {communicationFeatures.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-3 text-gray-700">
                  <Check className="text-orange-500 flex-shrink-0" size={20} strokeWidth={3} />
                  <span className="text-left">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Video Section - Unified Communication */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-amber-800 to-amber-900 aspect-video max-w-4xl mx-auto">
            {!isVideo1Playing ? (
              <div className="relative w-full h-full">
                <img
                  src="https://img.youtube.com/vi/R6DnbSIhn8s/maxresdefault.jpg"
                  alt="Unified Communication Infrastructure Demo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://img.youtube.com/vi/R6DnbSIhn8s/hqdefault.jpg";
                  }}
                />
                <button
                  onClick={() => setIsVideo1Playing(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-all group"
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                    <Play size={32} className="text-orange-500 ml-1" fill="currentColor" />
                  </div>
                </button>
              </div>
            ) : (
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/R6DnbSIhn8s?autoplay=1&modestbranding=1&rel=0&showinfo=0"
                title="Unified Communication Infrastructure"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full absolute inset-0"
              ></iframe>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: AI Intelligence & Decision Engine */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              AI Intelligence & Decision Engine
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              The moment a message enters Qorvix, the AI begins real-time analysis of buyer intent, urgency level, product interest, objections, and financial readiness. The system does not rely on fixed scripts. It adapts questioning, tone, and recommendations dynamically based on behavioral signals and live conversation flow.
            </p>
            <div className="space-y-3 max-w-2xl mx-auto mb-12">
              {aiFeatures.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-3 text-gray-700">
                  <Check className="text-orange-500 flex-shrink-0" size={20} strokeWidth={3} />
                  <span className="text-left">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Video Section - AI Intelligence */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-amber-800 to-amber-900 aspect-video max-w-4xl mx-auto">
            {!isVideo2Playing ? (
              <div className="relative w-full h-full">
                <img
                  src="https://img.youtube.com/vi/pVt7SvSu46w/maxresdefault.jpg"
                  alt="AI Intelligence Demo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://img.youtube.com/vi/pVt7SvSu46w/hqdefault.jpg";
                  }}
                />
                <button
                  onClick={() => setIsVideo2Playing(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-all group"
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                    <Play size={32} className="text-orange-500 ml-1" fill="currentColor" />
                  </div>
                </button>
              </div>
            ) : (
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/pVt7SvSu46w?autoplay=1&modestbranding=1&rel=0&showinfo=0"
                title="AI Intelligence & Decision Engine"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full absolute inset-0"
              ></iframe>
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Behavioral Memory & Sales Recoveryy */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Behavioral Memory & Sales Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              Qorvix retains full memory of every conversation. If a prospect disengages after discussing price, availability, or next steps, the system resumes automatically from the exact drop-off point. Follow-ups are not generic broadcasts. They are timed, contextual, and behavior-driven to maximize return conversions.
            </p>
            <div className="space-y-3 max-w-2xl mx-auto mb-12">
              {behavioralFeatures.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-3 text-gray-700">
                  <Check className="text-orange-500 flex-shrink-0" size={20} strokeWidth={3} />
                  <span className="text-left">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Video Section - Behavioral Memory */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-amber-800 to-amber-900 aspect-video max-w-4xl mx-auto">
            {!isVideo3Playing ? (
              <div className="relative w-full h-full">
                <img
                  src="https://img.youtube.com/vi/fRVY2-Tailc/maxresdefault.jpg"
                  alt="Behavioral Memory Demo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://img.youtube.com/vi/fRVY2-Tailc/hqdefault.jpg";
                  }}
                />
                <button
                  onClick={() => setIsVideo3Playing(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-all group"
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                    <Play size={32} className="text-orange-500 ml-1" fill="currentColor" />
                  </div>
                </button>
              </div>
            ) : (
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/fRVY2-Tailc?autoplay=1&modestbranding=1&rel=0&showinfo=0"
                title="Behavioral Memory & Sales Recovery"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full absolute inset-0"
              ></iframe>
            )}
          </div>
        </div>
      </section>

      {/* Section 4: Execution, Booking & Payment Automation */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Execution, Booking & Payment Automation
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              Once buyer readiness is confirmed, Qorvix automatically executes booking, test drives, consultations, or service sessions without staff involvement. For businesses that require deposits or commitment fees, Qorvix sends secure payment links, verifies transactions, and updates deal stages instantly.
            </p>
            <div className="space-y-3 max-w-2xl mx-auto mb-12">
              {executionFeatures.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-3 text-gray-700">
                  <Check className="text-orange-500 flex-shrink-0" size={20} strokeWidth={3} />
                  <span className="text-left">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Calendar/Booking Screenshot Placeholder */}
          <div className="max-w-4xl mx-auto">
            <img
              src="https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=1200&h=600&fit=crop"
              alt="Booking System Dashboard"
              className="dashboard-image w-full"
            />
          </div>
        </div>
      </section>

      {/* Section 5: Business Intelligence & Revenue Control */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Business Intelligence & Revenue Control
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              Qorvix operates a live CRM and revenue intelligence layer beneath every conversation. Every message, appointment, and payment updates live performance metrics. You gain full visibility into ad ROI, agent productivity, deal velocity, conversion ratios, and revenue attribution in real time.
            </p>
            <div className="space-y-3 max-w-2xl mx-auto mb-12">
              {intelligenceFeatures.map((feature, index) => (
                <div key={index} className="flex items-center justify-center gap-3 text-gray-700">
                  <Check className="text-orange-500 flex-shrink-0" size={20} strokeWidth={3} />
                  <span className="text-left">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Analytics Dashboard Screenshot */}
          <div className="max-w-5xl mx-auto">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&h=700&fit=crop"
              alt="Analytics Dashboard"
              className="dashboard-image w-full"
            />
          </div>
        </div>
      </section>

      {/* Final System Outcome */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Final System Outcome
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-12">
            Qorvix replaces fragmented tools, slow human response, forgotten follow-ups, manual scheduling, and blind reporting with one autonomous revenue engine that operates continuously without friction.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {systemFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="font-semibold text-gray-900">{feature}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">
                <span className="text-gradient">Qorvix</span> — Automate What Matters
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>London, England, UK</p>
                <p>+44 7757 233260</p>
                <p>Support@qorvix.ai</p>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Resources</h4>
              <div className="space-y-2 text-sm">
                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Documentation
                </Link>

                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Integration
                </Link>

                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  FAQs
                </Link>

                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Help Center
                </Link>

                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Roadmap
                </Link>
              </div>
            </div>
          </div>

          <div className="pt-8 flex items-center justify-between border-t border-gray-200">
            <div className="text-2xl font-bold text-gradient">Qorvix</div>
            <div className="text-sm text-gray-600">
              © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
            </div>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      <SignupFormModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        buttonType={modalButtonType}
      />
    </div>
  );
}